import React, { useEffect, useState } from 'react';
import { Moment } from 'moment';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { DatePicker as BaseDatePicker } from '@mui/x-date-pickers/DatePicker';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import { InputLabel } from '@mui/material';
import LoadingIndicator from '../loading/LoadingIndicator';
import FeedbackLabel from '../labels/FeedbackLabel';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useOrderMethodsStatus from '../../hooks/availability/useOrderMethodsStatus';
import { styled } from '@mui/material/styles';
import useOrderSlots from '../../hooks/utility/useOrderSlots';
import { useLocationData } from '../../modals/order-method/provider/LocationDataProvider';

interface Props {
  isSelectedTimeInvalid: boolean;
  setIsSelectedTimeInvalid: (value: boolean) => void;
}

const DatePicker = styled(BaseDatePicker)(() => ({
  '& .MuiInputBase-input[disabled]': {
    backgroundColor: 'transparent',
  },
}));

export default function ScheduledOrderInfo(props: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Order:ScheduledOrderInfo' });

  const { isSelectedTimeInvalid, setIsSelectedTimeInvalid } = props;
  const { setScheduledFor, scheduledFor, orderMethod, company } = useLocationData();
  const scheduleTime = scheduledFor ? moment(scheduledFor) : null;
  const setScheduleTime = (value: Moment) => {
    setScheduledFor(value.format());
  };
  const { formatTime } = useAppMethods();
  const { isOrderMethodOpenForFutureOrders } = useOrderMethodsStatus(company);

  const [currentMonth, setCurrentMonth] = useState<Moment>(scheduleTime ?? moment());
  const {
    orderSlots: slots,
    isSlotsLoading: loadingSlots,
    isHoliday,
    isHolidaysLoading: loadingDisabledDates,
  } = useOrderSlots({
    orderMethod: orderMethod,
    startDate: currentMonth.format('YYYY-MM'),
    endDate: currentMonth.format('YYYY-MM'),
    date: scheduleTime,
  });

  useEffect(() => {
    setIsSelectedTimeInvalid(
      scheduleTime &&
        !loadingDisabledDates &&
        (scheduleTime.isBefore(moment()) ||
          (scheduleTime.isSame(currentMonth, 'month') && isHoliday(scheduleTime.toISOString()))),
    );
  }, [scheduleTime, loadingDisabledDates]);

  useEffect(() => {
    const firstAvailable = slots?.[0]?.start_time;

    if (!firstAvailable) {
      return;
    }

    const hasSelectedSlot = scheduleTime && slots.some((slot) => scheduleTime.isSame(slot.start_time));

    if (hasSelectedSlot) {
      return;
    }

    setScheduleTime(moment(firstAvailable));
  }, [slots]);

  const renderTimePicker = () => {
    if (loadingSlots) {
      return (
        <div className="flex justify-center">
          <LoadingIndicator />
        </div>
      );
    }

    return (
      <FormControl>
        <InputLabel id="time-select-label">{t('labels.time')}</InputLabel>
        <Select
          labelId="time-select-label"
          id="time-select"
          value={scheduleTime?.unix() ?? ''}
          label={t('labels.time')}
          onChange={(e) => setScheduleTime(moment.unix(e.target.value as number))}
          MenuProps={{
            style: { zIndex: 9999 },
          }}
        >
          {slots.map((item) => (
            <MenuItem value={moment(item.start_time).unix()} key={`scheduled-order-slot-${item.start_time}`}>
              {formatTime(item.start_time)}
              {' - '}
              {formatTime(item.end_time)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const shouldDisableDate = (date: Moment) => isHoliday(date.toISOString());

  return (
    company?.has_scheduled_orders && (
      <div className="mt-md">
        <div className="grid grid-cols-2 gap-2">
          <DatePicker
            label={t('labels.date')}
            value={scheduleTime}
            onChange={setScheduleTime}
            onMonthChange={(date) => setCurrentMonth(date)}
            format="dddd, DD MMM"
            views={['day']}
            slotProps={{
              dialog: {
                style: { zIndex: 9999 },
              },
              popper: {
                style: { zIndex: 9999 },
              },
            }}
            shouldDisableDate={shouldDisableDate}
            disabled={!isOrderMethodOpenForFutureOrders(orderMethod)}
            disablePast
            loading={loadingDisabledDates}
            renderLoading={() => <LoadingIndicator />}
          />
          {renderTimePicker()}
        </div>
        {isSelectedTimeInvalid && !loadingSlots && (
          <FeedbackLabel message={t('feedback.selected_time_invalid')} type="Notice" className="mt-md" />
        )}
      </div>
    )
  );
}
